export const BILLING_PLAN_CODES = [
  "contractor_directory",
  "contractor_pro",
  "agent_membership",
  "property_14_days",
  "property_30_days",
] as const;

export type BillingPlanCode = (typeof BILLING_PLAN_CODES)[number];
export type BillingProductType = "membership" | "property_listing";
export type BillingInterval = "month" | "one_time";
export type BillingInvoiceStatus = "draft" | "open" | "paid" | "overdue" | "void" | "refunded";
export type BillingSubscriptionStatus =
  | "pending"
  | "active"
  | "grace"
  | "paused"
  | "cancelled"
  | "expired";

export type BillingPlan = {
  code: BillingPlanCode;
  productType: BillingProductType;
  eligibleRole: "auftragnehmer" | "immobilienmakler" | null;
  priceMinor: number;
  regularPriceMinor: number;
  currency: "CHF";
  billingInterval: BillingInterval;
  periodMonths: number | null;
  durationDays: number | null;
  directoryAccess: boolean;
  jobContactAccess: boolean;
};

export type BillingAccess = {
  billingEnabled: boolean;
  planCode: BillingPlanCode | null;
  subscriptionStatus: BillingSubscriptionStatus | null;
  currentPeriodEnd: string | null;
  accessSource: "subscription" | "lifetime" | null;
  directoryAccess: boolean;
  jobContactAccess: boolean;
};

export type BillingAddress = {
  street: string;
  buildingNumber: string;
  postalCode: string;
  city: string;
  countryCode: string;
};

export type BillingSettings = {
  billingEnabled: boolean;
  invoicePrefix: string;
  issuerName: string;
  issuerAddressLine: string | null;
  issuerPostalCode: string;
  issuerCity: string;
  issuerCountryCode: string;
  iban: string;
  qrIban: string | null;
  twintPaymentUrl: string | null;
  pricesIncludeVat: boolean;
  vatRateBasisPoints: number | null;
  paymentDueDays: number;
};

export type BillingInvoiceSummary = {
  id: string;
  userId: string | null;
  submissionId: string | null;
  invoiceNumber: string | null;
  planCode: BillingPlanCode;
  status: BillingInvoiceStatus;
  amountMinor: number;
  subtotalMinor: number;
  catalogAmountMinor: number;
  discountMinor: number;
  discountCode: string | null;
  discountPercent: number | null;
  discountEndsOn: string | null;
  settlementKind: "payment" | "discount" | null;
  currency: "CHF";
  customerName: string;
  customerCompany: string | null;
  customerEmail: string;
  billingAddress: BillingAddress;
  taxRateBasisPoints: number | null;
  pricesIncludeVat: boolean;
  paymentReference: string | null;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  servicePeriodStart: string | null;
  servicePeriodEnd: string | null;
  creditNoteNumber: string | null;
  creditedAt: string | null;
  refundReference: string | null;
  refundReason: string | null;
  createdAt: string;
};

export type BillingSubscriptionSummary = {
  id: string;
  planCode: BillingPlanCode;
  status: BillingSubscriptionStatus;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
};

export type BillingOverview = {
  invoices: BillingInvoiceSummary[];
  subscription: BillingSubscriptionSummary | null;
  settings: BillingSettings | null;
};

export type PublicBillingDocument = {
  invoice: BillingInvoiceSummary;
  settings: BillingSettings;
  plan: BillingPlan;
};

export type BillingInvoiceRequest = {
  planCode: BillingPlanCode;
  discountCode?: string;
  expectedAmount?: number;
  customerName: string;
  customerCompany?: string;
  billingAddress: BillingAddress;
};

export function formatBillingPrice(amountMinor: number, locale: string, currency = "CHF") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
  }).format(amountMinor / 100);
}

function billingDate(value: string) {
  return new Date(`${value.slice(0, 10)}T12:00:00.000Z`);
}

export function formatBillingPeriod(start: string | null, end: string | null, locale = "de-CH") {
  if (!start || !end) return null;
  const startDate = billingDate(start);
  const endDate = billingDate(end);
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
  const range = `${dateFormatter.format(startDate)} – ${dateFormatter.format(endDate)}`;
  if (
    startDate.getUTCFullYear() === endDate.getUTCFullYear() &&
    startDate.getUTCMonth() === endDate.getUTCMonth()
  ) {
    const month = new Intl.DateTimeFormat(locale, {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(startDate);
    return `${month} (${range})`;
  }
  return range;
}
